import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, X, Send, Loader2, Check, ArrowRight, Trash2, MessageSquare, Mic, MicOff } from "lucide-react";
import { toast } from "sonner";

/**
 * TARU Copilot — floating AI assistant.
 *
 *  - Bottom-right bubble opens a chat panel.
 *  - Cmd/Ctrl-K opens it from anywhere.
 *  - Model runs tool-calls server-side (RBAC-scoped).
 *  - Write actions arrive as `proposed_action` — user must confirm.
 *  - Navigate hints deep-link into the app.
 */
export function Copilot() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role, content, proposed?, navigate?}
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(() => `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`);
  const scrollRef = useRef(null);

  const suggestions = useMemo(() => {
    const role = user?.role || "";
    if (role === "production" || role === "karigar") {
      return [
        "Show me my current job stage",
        "How many jobs are in polishing right now?",
        "What's my department queue?",
      ];
    }
    if (role === "sales") {
      return [
        "Show my follow-ups this week",
        "Find contacts in Mumbai",
        "Add a new lead named …",
      ];
    }
    return [
      "How many orders are in production?",
      "Show orders delivered this month",
      "Find contact with mobile 9876…",
      "Production summary by stage",
    ];
  }, [user]);

  // Cmd/Ctrl-K shortcut
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === "Escape" && open) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  if (!user) return null;

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    const nextMsgs = [...messages, { role: "user", content: msg }];
    setMessages(nextMsgs);
    setBusy(true);

    // Placeholder assistant bubble filled progressively.
    const asstIdx = nextMsgs.length;
    setMessages([...nextMsgs, { role: "assistant", content: "", tool_steps: [], streaming: true }]);

    // Try SSE streaming first; fall back to /chat on any failure.
    let streamed = false;
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const tokenKey = localStorage.getItem("token") ? "token" : null;
      const token = tokenKey ? localStorage.getItem(tokenKey) : null;
      const res = await fetch(`${backend}/api/copilot/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
        credentials: "include",
      });
      if (!res.ok || !res.body) throw new Error(`stream ${res.status}`);
      streamed = true;
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      const patch = (extra) => setMessages((ms) => ms.map((m, i) => i === asstIdx ? { ...m, ...extra } : m));
      const appendStep = (step) => setMessages((ms) => ms.map((m, i) => i === asstIdx ? { ...m, tool_steps: [...(m.tool_steps || []), step] } : m));
      // Read chunks
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) !== -1) {
          const frame = buf.slice(0, idx); buf = buf.slice(idx + 2);
          const lines = frame.split("\n");
          let ev = null, data = "";
          for (const line of lines) {
            if (line.startsWith("event: ")) ev = line.slice(7).trim();
            else if (line.startsWith("data: ")) data += line.slice(6);
          }
          if (!ev) continue;
          let p = {};
          try { p = JSON.parse(data || "{}"); } catch (_) { /* noop */ }
          if (ev === "thinking") appendStep({ phase: "thinking" });
          else if (ev === "tool_call") appendStep({ phase: "tool_call", name: p.name, arguments: p.arguments });
          else if (ev === "tool_result") appendStep({ phase: "tool_result", name: p.name, ok: p.ok, summary: p.summary });
          else if (ev === "reply") patch({ content: p.text || "" });
          else if (ev === "done") patch({
            content: p.reply || "",
            proposed: p.proposed_action || null,
            navigate: p.navigate || null,
            streaming: false,
          });
          else if (ev === "error") patch({ content: `Sorry — ${p.detail || "stream error"}`, streaming: false });
        }
      }
    } catch (streamErr) {
      // Fallback to blocking endpoint
    }

    if (!streamed) {
      try {
        const { data } = await api.post("/copilot/chat", { session_id: sessionId, message: msg });
        setMessages((ms) => ms.map((m, i) => i === asstIdx ? {
          role: "assistant",
          content: data.reply || "",
          proposed: data.proposed_action || null,
          navigate: data.navigate || null,
          streaming: false,
        } : m));
      } catch (e) {
        const detail = formatApiErrorDetail(e.response?.data?.detail) || "Copilot failed to respond.";
        setMessages((ms) => ms.map((m, i) => i === asstIdx ? { role: "assistant", content: `Sorry — ${detail}`, streaming: false } : m));
      }
    }
    setBusy(false);
  };

  const confirmAction = async (idx, proposed) => {
    setBusy(true);
    try {
      const { data } = await api.post("/copilot/confirm", {
        action: proposed.action,
        arguments: proposed.arguments || {},
        session_id: sessionId,
      });
      setMessages((ms) => ms.map((m, i) => i === idx ? { ...m, confirmedResult: data, proposed: null } : m));
      toast.success(`Action completed`);
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || "Action failed";
      toast.error(detail);
      setMessages((ms) => ms.map((m, i) => i === idx ? { ...m, actionError: detail } : m));
    } finally {
      setBusy(false);
    }
  };

  const rejectAction = (idx) => {
    setMessages((ms) => ms.map((m, i) => i === idx ? { ...m, proposed: null, actionCancelled: true } : m));
  };

  const goNavigate = (nav) => {
    if (!nav?.path) return;
    setOpen(false);
    navigate(nav.path);
  };

  const resetChat = async () => {
    try {
      await api.delete(`/copilot/history/${sessionId}`);
    } catch (_) { /* noop */ }
    setMessages([]);
    setSessionId(`s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`);
  };

  return (
    <>
      {/* Floating trigger */}
      <button
        onClick={() => setOpen(true)}
        className="fixed z-40 bottom-24 right-4 lg:bottom-6 lg:right-6 h-14 w-14 rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white shadow-xl flex items-center justify-center transition-transform hover:scale-105 border-2 border-[#C9A24B]"
        aria-label="Open TARU Copilot"
        data-testid="copilot-trigger"
      >
        <Sparkles size={22} strokeWidth={1.8} />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end lg:items-end justify-center lg:justify-end p-0 lg:p-6 bg-black/40 lg:bg-black/20 backdrop-blur-sm" data-testid="copilot-panel">
          <div
            className="w-full lg:w-[420px] h-[85vh] lg:h-[70vh] max-h-[720px] bg-[#FAF7EF] border-t lg:border border-[#0F5E39]/40 rounded-t-3xl lg:rounded-3xl shadow-2xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-4 py-3 bg-[#083F22] text-white flex items-center gap-2">
              <Sparkles size={18} className="text-[#C9A24B]" />
              <div>
                <div className="font-serif text-lg leading-tight">TARU Copilot</div>
                <div className="text-[10px] uppercase tracking-widest text-[#C9A24B]/80">Your AI shop-floor assistant</div>
              </div>
              <div className="ml-auto flex items-center gap-1">
                <button onClick={resetChat} className="p-2 rounded-full hover:bg-white/10" title="New chat" data-testid="copilot-reset">
                  <Trash2 size={16} />
                </button>
                <button onClick={() => setOpen(false)} className="p-2 rounded-full hover:bg-white/10" title="Close" data-testid="copilot-close">
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Body */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {messages.length === 0 && (
                <div className="text-center py-8">
                  <MessageSquare size={36} className="mx-auto text-[#0F5E39]" strokeWidth={1.5}/>
                  <div className="font-serif text-xl mt-3">How can I help?</div>
                  <p className="text-xs text-[#4A5A50] mt-1">
                    Ask about orders, contacts, follow-ups or navigate anywhere. Press <kbd className="px-1 rounded bg-[#F1EDDF] border border-[#E4DFCF] text-[10px]">Ctrl</kbd> + <kbd className="px-1 rounded bg-[#F1EDDF] border border-[#E4DFCF] text-[10px]">K</kbd> anytime.
                  </p>
                  <div className="mt-4 grid gap-2">
                    {suggestions.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => send(s)}
                        className="text-left text-xs px-3 py-2 rounded-full border border-[#E4DFCF] bg-white hover:border-[#0F5E39]/40 hover:bg-[#F6F1E1]/50"
                        data-testid={`copilot-suggest-${i}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, idx) => (
                <MessageBubble
                  key={idx}
                  msg={m}
                  onConfirm={() => confirmAction(idx, m.proposed)}
                  onReject={() => rejectAction(idx)}
                  onNavigate={goNavigate}
                />
              ))}
              {busy && (
                <div className="flex items-center gap-2 text-[#4A5A50] text-sm">
                  <Loader2 size={14} className="animate-spin" /> Thinking…
                </div>
              )}
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="border-t border-[#E4DFCF] px-3 py-2 bg-white flex items-end gap-2"
            >
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                placeholder="Ask anything…"
                className="min-h-[40px] max-h-32 resize-none border-[#E4DFCF]"
                data-testid="copilot-input"
                disabled={busy}
              />
              <Button
                type="submit"
                disabled={busy || !input.trim()}
                className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white h-10 w-10 p-0 shrink-0"
                data-testid="copilot-send"
              >
                {busy ? <Loader2 size={16} className="animate-spin"/> : <Send size={16}/>}
              </Button>
              <VoiceInput onText={(t) => setInput((prev) => (prev ? prev + " " : "") + t)}/>
            </form>
          </div>
        </div>
      )}
    </>
  );
}


function MessageBubble({ msg, onConfirm, onReject, onNavigate }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] ${isUser ? "" : "w-full"}`}>
        <div className={`px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${isUser
          ? "bg-[#0F5E39] text-white rounded-br-sm"
          : "bg-white border border-[#E4DFCF] text-[#0F1E15] rounded-bl-sm"}`}
        >
          {msg.content}
          {msg.streaming && !msg.content && (
            <span className="text-[#4A5A50] italic text-xs">Thinking…</span>
          )}
        </div>

        {msg.tool_steps && msg.tool_steps.length > 0 && (
          <div className="mt-1 space-y-0.5" data-testid="copilot-tool-steps">
            {msg.tool_steps.filter((s) => s.phase !== "thinking").slice(-4).map((s, i) => (
              <div key={i} className="text-[10px] uppercase tracking-widest text-[#4A5A50] flex items-center gap-1">
                <span className={`inline-block h-1.5 w-1.5 rounded-full ${s.ok === false ? "bg-red-500" : "bg-[#C9A24B]"}`}/>
                <span>{s.name}</span>
                {s.summary && <span className="normal-case tracking-normal text-[#4A5A50]/70">· {s.summary}</span>}
              </div>
            ))}
          </div>
        )}

        {msg.proposed && (
          <div className="mt-2 rounded-xl border border-[#C9A24B] bg-[#FFF6E1] p-3" data-testid="copilot-proposed-action">
            <div className="text-[10px] uppercase tracking-widest text-[#8A6600] font-medium">Confirm to proceed</div>
            <div className="text-sm text-[#0F1E15] mt-1">{msg.proposed.human_summary}</div>
            <details className="mt-2 text-xs text-[#4A5A50]">
              <summary className="cursor-pointer">Preview data</summary>
              <pre className="mt-1 text-[10px] bg-[#F6F1E1] p-2 rounded overflow-x-auto">{JSON.stringify(msg.proposed.arguments, null, 2)}</pre>
            </details>
            <div className="flex gap-2 mt-2">
              <Button size="sm" className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white h-8 px-3" onClick={onConfirm} data-testid="copilot-confirm-btn">
                <Check size={14} className="mr-1"/> Confirm
              </Button>
              <Button size="sm" variant="outline" className="rounded-full h-8 px-3" onClick={onReject} data-testid="copilot-reject-btn">
                <X size={14} className="mr-1"/> Cancel
              </Button>
            </div>
          </div>
        )}

        {msg.confirmedResult && (
          <div className="mt-2 rounded-xl border border-[#0F5E39]/30 bg-[#E1F0E6] p-2 text-xs text-[#0F5E39]" data-testid="copilot-action-done">
            <Check size={14} className="inline mr-1"/> Done.
            {msg.confirmedResult.id && <span className="ml-1 opacity-70 font-mono">#{String(msg.confirmedResult.id).slice(0,8)}</span>}
          </div>
        )}

        {msg.actionCancelled && (
          <div className="mt-2 text-xs text-[#4A5A50] italic">Cancelled.</div>
        )}

        {msg.actionError && (
          <div className="mt-2 rounded-xl border border-red-200 bg-red-50 p-2 text-xs text-red-700">{msg.actionError}</div>
        )}

        {msg.navigate && (
          <button
            onClick={() => onNavigate(msg.navigate)}
            className="mt-2 inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-white border border-[#0F5E39]/30 text-[#0F5E39] hover:bg-[#F6F1E1]"
            data-testid="copilot-nav-btn"
          >
            <ArrowRight size={12}/> Open {msg.navigate.label}
          </button>
        )}
      </div>
    </div>
  );
}


/**
 * VoiceInput — browser SpeechRecognition (Chrome, Edge) push-to-talk mic.
 * Silently no-ops on Firefox/Safari desktop where the API is missing.
 */
function VoiceInput({ onText }) {
  const [listening, setListening] = React.useState(false);
  const [supported, setSupported] = React.useState(true);
  const recRef = React.useRef(null);

  const start = () => {
    const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Rec) { setSupported(false); return; }
    const rec = new Rec();
    rec.lang = "en-IN";
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      const text = e.results?.[0]?.[0]?.transcript || "";
      if (text) onText(text);
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recRef.current = rec;
    setListening(true);
    try { rec.start(); } catch (_) { setListening(false); }
  };
  const stop = () => {
    try { recRef.current?.stop(); } catch (_) { /* noop */ }
    setListening(false);
  };

  if (!supported) return null;
  return (
    <button
      type="button"
      onClick={listening ? stop : start}
      className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 border ${listening ? "bg-red-500 text-white border-red-600 animate-pulse" : "bg-white text-[#0F5E39] border-[#E4DFCF] hover:border-[#0F5E39]/40"}`}
      title={listening ? "Stop voice input" : "Voice input"}
      data-testid="copilot-mic"
    >
      {listening ? <MicOff size={14}/> : <Mic size={14}/>}
    </button>
  );
}

